import { Theme, ThemeOptions } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Shadows } from '@mui/material/styles/shadows';
import { vividPurple, darkNavy, blue, common, red, yellow, orange, purple, green, vividGreen } from '../color-pallete';
import typography from '../typography';
import { breakpoints } from '../breakpoints';
import components from '../components';
import { createStateLayer } from '../utils';

export const lightTheme = {
  breakpoints,
  createStateLayer(
    this: Theme,
    containerColor: string,
    outlineColor: string,
  ) {
    return createStateLayer(this, containerColor, outlineColor);
  },
  palette: {
    primary: {
      main: vividPurple[600],
      onMain: vividPurple[990],
      container: vividPurple[950],
      onContainer: vividPurple[400],
      inverse: vividPurple[300],
      onInverse: vividPurple[990],
      dark: vividPurple[300],
      light: vividPurple[800],
      outline: vividPurple[600],
      outlineVariant: vividPurple[900],
      //Required by mui, don't use
      contrastText: vividPurple[990],
    },
    secondary: {
      main: darkNavy[300],
      onMain: darkNavy[980],
      container: darkNavy[940],
      onContainer: darkNavy[300],
      inverse: darkNavy[100],
      onInverse: common.white,
      dark: darkNavy[200],
      light: darkNavy[700],
      outline: darkNavy[400],
      outlineVariant: darkNavy[900],
      //Required by mui, don't use
      contrastText: darkNavy[980],
    },
    error: {
      main: red[500],
      onMain: red[990],
      container: red[950],
      onContainer: red[300],
      inverse: red[500],
      onInverse: red[990],
      dark: red[300],
      light: red[700],
      outline: red[500],
      outlineVariant: red[900],
      //Required by mui, don't use
      contrastText: red[990],
    },
    warning: {
      main: yellow[500],
      onMain: yellow[100],
      container: yellow[900],
      onContainer: yellow[100],
      inverse: yellow[300],
      onInverse: yellow[990],
      dark: yellow[300],
      light: yellow[700],
      outline: yellow[400],
      outlineVariant: yellow[700],
      //Required by mui, don't use
      contrastText: yellow[100],
    },
    info: {
      main: blue[700],
      onMain: blue[200],
      container: blue[950],
      onContainer: blue[200],
      inverse: blue[300],
      onInverse: blue[990],
      dark: blue[300],
      light: blue[700],
      outline: blue[500],
      outlineVariant: blue[900],
      //Required by mui, don't use
      contrastText: blue[200],
    },
    success: {
      main: green[300],
      onMain: green[990],
      container: green[950],
      onContainer: green[200],
      inverse: green[300],
      onInverse: green[990],
      dark: green[300],
      light: green[800],
      outline: green[400],
      outlineVariant: green[800],
      //Required by mui, don't use
      contrastText: green[990],
    },
    orange: {
      main: orange[700],
      onMain: orange[200],
      container: orange[950],
      onContainer: orange[50],
      inverse: orange[100],
      onInverse: orange[990],
      dark: orange[400],
      light: orange[700],
      outline: orange[500],
      outlineVariant: orange[900],
      //Required by mui, don't use
      contrastText: orange[200],
    },
    purple: {
      main: purple[500],
      onMain: purple[990],
      container: purple[950],
      onContainer: purple[300],
      inverse: purple[400],
      onInverse: purple[990],
      dark: purple[300],
      light: purple[700],
      outline: purple[500],
      outlineVariant: purple[800],
      //Required by mui, don't use
      contrastText: purple[990],
    },
    brand: {
      main: vividGreen[500],
      onMain: vividGreen[100],
      container: vividGreen[950],
      onContainer: vividGreen[200],
      inverse: vividGreen[300],
      onInverse: vividGreen[990],
      dark: vividGreen[300],
      light: vividGreen[600],
      outline: vividGreen[500],
      outlineVariant: vividGreen[800],
      //Required by mui, don't use
      contrastText: vividGreen[100],
    },
    neutral: {
      //container
      surface: darkNavy[990],
      surfaceDim: darkNavy[910],
      surfaceBright: common.white,
      surfaceVariant: darkNavy[800],
      inverse: darkNavy[50],
      surfaceTint: vividPurple[500],
      containerLowest: darkNavy[920],
      containerLow: darkNavy[960],
      container: darkNavy[980],
      containerHigh: darkNavy[990],
      containerHighest: common.white,
      background: darkNavy[980],
      //on
      onSurface: darkNavy[100],
      onSurfaceVariant: darkNavy[300],
      onInverse: darkNavy[900],
      onBackground: darkNavy[300],
      disabled: darkNavy[800],
      //outline
      outline: darkNavy[400],
      outlineVariant: darkNavy[900],
      //extras
      shadow: darkNavy[50],
      scrim: darkNavy[300],
      fixed: darkNavy[300],
      dark: darkNavy[50],
      //required by mui, don't use
      main: darkNavy[100],
      contrastText: darkNavy[100],
    },
    text: {
      primary: darkNavy[50],
      secondary: darkNavy[300],
    },
    common,
    background: {
      default: common.white,
      paper: common.white,
    },
    divider: darkNavy[900],
    action: {
      hoverOpacity: 0.08,
      focusOpacity: 0.12,
      activatedOpacity: 0.12,
      dragOpacity: 0.16,
      disabledOpacity: 0.38,
      disabled: alpha(darkNavy[100], 0.38),
      disabledBackground: alpha(darkNavy[400], 0.12),
      disabledBorder: alpha(darkNavy[100], 0.12),
    }
  },
  components,
  typography,
  shape: {
    borderRadius: 8
  },
  shadows: (() => {
    //MUI requires to have 25 shadows, but curently in our design system is 6;
    const shadows = new Array(25).fill('0px 8px 10px 0px rgba(5, 7, 15, 0.04), 0px 24px 32px 4px rgba(5, 7, 15, 0.07)') as Shadows;
    shadows[0] = 'none';
    shadows[1] = '0px 1px 1px 0px rgba(5, 7, 15, 0.04), 0px 1px 4px 0px rgba(0, 0, 0, 0.07)';
    shadows[2] = '0px 2px 4px 0px rgba(5, 7, 15, 0.04), 0px 4px 8px 1px rgba(0, 0, 0, 0.07)';
    shadows[3] = '0px 4px 6px 0px rgba(5, 7, 15, 0.04), 0px 8px 16px 2px rgba(0, 0, 0, 0.07)';
    shadows[4] = '0px 6px 8px 0px rgba(5, 7, 15, 0.04), 0px 16px 24px 3px rgba(0, 0, 0, 0.07)';
    shadows[5] = '0px 8px 10px 0px rgba(5, 7, 15, 0.04), 0px 24px 32px 4px rgba(5, 7, 15, 0.07)';
    return shadows;
  })()
} as ThemeOptions;